import {
  inject,
  DOM,
  PLATFORM,
  computedFrom,
  customElement,
  bindingBehavior,
  bindingMode,
  bindable,
  inlineView,
  containerless,
  TaskQueue
} from 'aurelia-framework';
import {
  SelectEvent,
  RemoveEvent,
  LeaveEvent,
  EditEvent,
  JoinEvent,
  AcceptEvent,
  CreateEvent
} from '../../../broadcast/group-events';
import {GroupService, defaultGroupId} from '../../../services/group/group-service';
import {CurrentGroup} from '../../../services/group/current-group';
import {ContentService} from '../../../services/content-service';
import * as _ from 'lodash';
import {I18N} from 'aurelia-i18n';
import {GroupEventBus} from '../../../broadcast/group-event-bus';
import {EventAggregator, Subscription} from 'aurelia-event-aggregator';
import publicGroupImg from './img/home-group.svg';
import connectionsGroupImg from './img/connections-group.svg';
import {SmDeviceDetected} from '../../../tools/sm-device-detected';
import {isIE} from '../../../tools/ie-tools';
import {HttpResponseMessage} from "aurelia-http-client";

@customElement('group-pane')
@inject(
  DOM.Element,
  GroupService,
  ContentService,
  TaskQueue,
  I18N,
  EventAggregator,
  GroupEventBus,
  CurrentGroup,
  SmDeviceDetected
)
export class GroupPane {

  groupList = [];
  nestedPane = false;
  nestedHovered = false;
  nestedSelected = false;
  nestedGroupId = null;
  nestedCanAddGroup = false;
  selectedGroup = new GroupSelection();
  isLoading = false;
  isVisible = false;
  paneTop = 0;
  paneHeight = 0;
  targetClientTop = 0;

  __groupOffset = 0;

  @bindable({
    defaultBindingMode: bindingMode.oneTime
  }) isRoot = false;

  @bindable({
    defaultBindingMode: bindingMode.oneTime
  }) nestedLevel = 0;

  @bindable({
    defaultBindingMode: bindingMode.oneWay
  }) parentGroupId = null;

  @bindable({
    defaultBindingMode: bindingMode.twoWay
  }) hovered = false;

  @bindable({
    defaultBindingMode: bindingMode.twoWay
  }) selected = false;

  @bindable({
    defaultBindingMode: bindingMode.oneWay
  }) parentPaneTop = 0;

  @bindable({
    defaultBindingMode: bindingMode.oneWay
  }) parentTargetClientTop = 0;

  @bindable({
    defaultBindingMode: bindingMode.oneWay
  }) rootClientTop = 0;

  @bindable({
    defaultBindingMode: bindingMode.oneWay
  }) rootClientBottom = 0;

  @bindable canAddGroup = false;

  @bindable({
    defaultBindingMode: bindingMode.oneTime
  }) groupSearchResult: any = {};

  ieSearch: EventListener;
  isAnimated: boolean;
  searchOpened: boolean;
  searchFocused: boolean;
  private __selectionSubscription: Subscription;
  private __editSubscription: Subscription;
  private __removeSubscription: Subscription;
  private __addingSubscription: Subscription;
  private __dragRequest: Promise<HttpResponseMessage>;
  private __childListRequest: PromiseWithCancel<HttpResponseMessage>;
  private __nestedGroupTimerId: number;
  private __hoverTimerId: number;
  cachedGroupList: any[];
  filterMask: string;
  inputField: HTMLInputElement;
  availableGroups: any[];

  constructor(private __groupPaneEl: HTMLElement, private __groupService: GroupService, private __contentService: ContentService, private __taskQueue: TaskQueue, private __i18n: I18N, private __ea: EventAggregator, private __groupEventBus, private __currentGroup: CurrentGroup, private __smDevice: SmDeviceDetected) {
    this.ieSearch = e => {
      const targetName = (e.target as HTMLElement).className;
      switch(targetName) {
        case 'search-input-field au-target':
          this.performSearch();
          break;
      }
    }
  }

  @computedFrom('isRoot', 'isAnimated', 'isVisible', 'canAddGroup')
  get clazz() {
    return `${!this.isRoot && this.isAnimated ? 'animated' : ''} ${this.isVisible ? 'visible' : ''} ${this.canAddGroup ? '' : 'not-admin'}`;
  }

  @computedFrom('paneHeight', 'paneTop', 'isRoot')
  get style() {
    if (this.isRoot) return '';
    return `${this.paneHeight ? `height:${this.paneHeight}px;` : ''}top:${this.paneTop}px;`;
  }

  @computedFrom('paneTop')
  get accumulatedTop() {
    return this.parentPaneTop + this.paneTop;
  }

  @computedFrom('rootClientBottom', 'rootClientTop')
  get maxHeight() {
    return this.rootClientBottom - this.rootClientTop;
  }

  bind() {
    if (this.isRoot) {
      this.canAddGroup = true;
      this.__loadRootGroups();
      this.searchOpened = false;
      this.searchFocused = false;
    } else {
      this.parentGroupIdChanged(this.parentGroupId);
    }
    if (this.__smDevice.smallDevice) {
      this.canAddGroup = false;
    }
    this.__handleBroadcastSelectEvent(this.__currentGroup.state);
    this.__selectionSubscription = this.__groupEventBus.subscribe(
      SelectEvent, msg => this.__handleBroadcastSelectEvent(msg), this);
    this.__editSubscription = this.__groupEventBus.subscribe(
      EditEvent, msg => this.__handleGroupEdit(msg), this);
    this.__removeSubscription = this.__groupEventBus.subscribeGroup(
      [LeaveEvent, RemoveEvent], msg => this.__handleGroupRemove(msg), this);
    this.__addingSubscription = this.__groupEventBus.subscribeGroup(
      [JoinEvent, AcceptEvent, CreateEvent], msg => this.__handleGroupAdding(msg), this);
  }

  attached() {
    if (this.isRoot) {
      this.rootClientTop = this.__groupPaneEl.getBoundingClientRect().top;
      this.updateRootClientBottom();
      this.__groupPaneEl.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.keyCode === 27 && this.searchFocused) {
          this.closeSearch();
        }
      });
    }
  }

  detached() {
    this.isAnimated = this.isVisible = false;
    this.__resetPanePosition();
    this.__cancelChildRequest();
    this.__cancelDragRequest();
    this.groupList = [];
  }

  unbind() {
    this.__editSubscription.dispose();
    this.__removeSubscription.dispose();
    this.__selectionSubscription.dispose();
    this.__addingSubscription.dispose();
  }

  dragAndDrop(dragIndex, dropIndex) {
    if (dragIndex !== dropIndex) {
      this.__cancelDragRequest();

      let target = this.groupList.splice(dragIndex, 1)[0];
      this.groupList.splice(dropIndex, 0, target);
      this.__dragRequest = this.__groupService.setGroupPosition(target.id, dropIndex - this.__groupOffset);
      this.__dragRequest.catch(response => {
        if (response.responseType !== 'abort') {
          // revert position changes on error
          let target1 = this.groupList.splice(dropIndex, 1)[0];
          this.groupList.splice(dragIndex, 0, target1);
        }
      });
    }
  }

  canDragOrDrop(context) {
    return _.get(context, 'item.id', -1) >= 0;
  }

  goHome(id) {
    return this.__contentService.showHome(id);
  }

  openSearch() {
    this.searchOpened = true;
    this.searchFocused = true;
    if(isIE()) {
      document.addEventListener("keyup", this.ieSearch);
    }
}

  closeSearch() {
    this.searchOpened = false;
    this.searchFocused = false;
    this.groupList = this.cachedGroupList;
    this.filterMask = '';
    this.inputField.textContent = '';
    this.groupSearchResult = {};
    if(isIE()) {
      document.removeEventListener("keyup", this.ieSearch);
    }
}

  performSearch() {
    if (_.isEmpty(this.filterMask.trim())) {
      this.groupList = this.cachedGroupList;
      this.groupSearchResult = {};
    } else {
      this.groupSearchResult = this.__findTargetGroups();
      this.__filterGroups(this.groupSearchResult.acceptedGroupsIds);
    }
  }

  /**
   * @param {Object} searchResult
   * @private
   */
  __filterNestedPane(searchResult, parentGroupId) {
    if (_.isEmpty(searchResult) || _.isEmpty(searchResult.targetGroupsIds) ||
      _.includes(searchResult.targetGroupsIds, parentGroupId)) {
      this.groupSearchResult = {};
      return;
    }
    this.__filterGroups(searchResult.acceptedGroupsIds);
  }

  /**
   * @private
   * @returns {Object}
   */
  __findTargetGroups() {
    const targetGroupsIds = this.availableGroups
      .filter(g =>
        this.filterMask.trim().toLowerCase().split(' ').every(s => g.name.toLowerCase().includes(s)))
      .map(group => group.id);
    const acceptedGroupsIds = _.uniq(targetGroupsIds.reduce((acc, id) => acc.concat(this.__findParents(id)), []));
    return { targetGroupsIds: targetGroupsIds, acceptedGroupsIds: acceptedGroupsIds };
  }

  /**
   * @param {Array} acceptedGroupsIds
   * @private
   */
  __filterGroups(acceptedGroupsIds) {
    this.groupList = _.isEmpty(acceptedGroupsIds) ?
      [] : this.cachedGroupList.filter(g => _.includes(acceptedGroupsIds, g.id));
  }

  /**
   * @param {number} groupId
   * @private
   * @returns {Array.<number>}
   */
  __findParents(groupId) {
    const f = (id, acc) => {
      if (id === 0) return acc;
      const group = this.availableGroups.find(g => g.id === id);
      return f(group.parent ? group.parent : 0, acc.concat(id));
    };
    return f(groupId, []);
  }

  /**
   * @return {Promise}
   * @private
   */
  __loadRootGroups() {
    this.isLoading = true;
    return this.__groupService.rootList().response.then(data => {
      this.groupList = this.__handleDefaultGroups(data);
      this.cachedGroupList = this.groupList;
      this.isLoading = false;
      this.__groupOffset = _.findIndex(this.groupList, i => i.id >= 0);
    }).then(() => this.__loadAvailableGroups()).catch(() => {
      return new Promise(resolve => _.delay(resolve, 5000))
        .then(() => this.__loadRootGroups());
    });
  }

  /**
   * @private
   * @returns {Promice}
   */
  __loadAvailableGroups() {
    return this.__groupService.availableGroupsList().response
      .then(data => this.availableGroups = data)
      .catch(() => {
        return new Promise(resolve => _.delay(resolve, 5000))
          .then(() => this.__loadAvailableGroups());
      });
  }

  /**
   * @param {Array} groups
   * @private
   * @returns {Array}
   */
  __handleDefaultGroups(groups) {
    groups.forEach((group, idx) => {
      if (group.id < 0) {
        group.name = this.__i18n.tr(`groups_select_wide.groups.${group.name.toLowerCase()}`);
        switch (group.id) {
        case defaultGroupId.PUBLIC:
          group.thumbLogoUrl = publicGroupImg;
          break;
        case defaultGroupId.CONNECTION:
          groups.splice(idx,1);
          //group.thumbLogoUrl = connectionsGroupImg;
          break;
        default: //do nothing
        }
      }
    });

    return groups;
  }

  parentGroupIdChanged(value) {
    this.groupList = [];
    this.__cancelChildRequest(false);

    this.isVisible = true;
    let loadingTimerId = _.delay(() => {
      this.isLoading = true;
      this.__taskQueue.queueTask(() => this.updatePaneHeight());
    }, 150);

    let {response, request} = this.__groupService.childList(value);
    response.then(data => {
      this.groupList = data;
      this.cachedGroupList = data;
      this.__filterNestedPane(this.groupSearchResult, this.parentGroupId);
      this.isLoading = false;
    }).finally(() => {
      PLATFORM.global.clearTimeout(loadingTimerId);
    }).catch(error => {
      if (this.isLoading && error.responseType !== 'abort') {
        this.isLoading = false;
        this.updatePaneHeight();
      }
    });

    this.__childListRequest = request;
  }

  parentPaneTopChanged() {
    this.updatePanePosition();
  }

  parentTargetClientTopChanged() {
    this.updatePanePosition();
  }

  rootClientTopChanged() {
    this.updatePaneHeight();
  }

  rootClientBottomChanged() {
    this.updatePaneHeight();
  }

  updatePanePosition() {
    if (this.isRoot) return;

    let targetTop = this.parentTargetClientTop - this.rootClientTop;
    this.paneTop = _.clamp(
      targetTop - this.paneHeight / 2 - this.parentPaneTop, // calculated value
      -this.parentPaneTop, // min value
      this.maxHeight - this.paneHeight // max value
    );
  }

  updatePaneHeight() {
    if (this.isRoot) return;

    this.__groupPaneEl.style.height = '';
    let offsetHeight = _.clamp(this.__groupPaneEl.offsetHeight, 0, this.maxHeight);
    this.__groupPaneEl.style.height = `${offsetHeight}px`;
    this.paneHeight = offsetHeight;
    this.updatePanePosition();

    if (!this.isAnimated) {
      this.__taskQueue.queueTask(() => this.isAnimated = true);
    }
  }

  /**
   * @param {boolean} isHovered
   * @param {number} id
   * @param {number} clientTop
   */
  handleGroupHover(isHovered, id, clientTop) {
    if (id < 0) {
      return;
    }

    if (isHovered) {
      clearTimeout(this.__nestedGroupTimerId);
      this.nestedGroupId = id;
      this.nestedPane = true;
      this.targetClientTop = clientTop;

      let item = _.find(this.groupList, i => i.id === this.nestedGroupId);
      this.nestedCanAddGroup = item.isAdmin || item.isGlobalAdmin;
    } else {
      this.__nestedGroupTimerId = _.delay(() => this.nestedPane = false, 350);
    }
  }

  handleGroupSelection({id, selected, direct}) {
    (!selected || !direct ? this.__groupService.selectGroup({
      groupId: id, caller: this
    }).then(() => {
      this.selectedGroup = new GroupSelection(id, true);
    }) : Promise.resolve()).then(() => {
      PLATFORM.global.Pages.canGoHome && this.goHome(id);
    });
  }

  __handleBroadcastSelectEvent(msg) {
    let group = _.get(msg, `groupPath[${this.nestedLevel}]`, {});
    let lastIndex = _.get(msg, 'groupPath.length', 0) - 1;
    this.selectedGroup = new GroupSelection(
      _.get(group, 'id', null), lastIndex === this.nestedLevel
    );
  }

  /**
   * @param {boolean} hovered
   */
  handleHover(hovered) {
    clearTimeout(this.__hoverTimerId);
    if (hovered) {
      this.hovered = true;
    } else {
      this.__hoverTimerId = _.delay(() => this.hovered = false, 350);
    }
  }

  updateRootClientBottom() {
    this.rootClientBottom = (DOM.querySelectorAll('body')[0] as HTMLElement).clientHeight;
  }

  createNewGroup() {
    if (!this.canAddGroup) {
      return;
    }

    if (this.isRoot) {
      this.__groupService.createRootGroup();
    } else {
      this.__groupService.createSubGroup(this.parentGroupId);
    }
  }

  /**
   * @param {boolean} [handleLoading=true]
   * @private
   */
  __cancelChildRequest(handleLoading = true) {
    if (this.__childListRequest) {
      this.__childListRequest.cancel();
      this.__childListRequest = null;

      if (handleLoading) {
        this.isLoading = false;
      }
    }
  }

  /**
   * @private
   */
  __cancelDragRequest() {
    if (this.__dragRequest) {
      // this.__dragRequest.cancel();
    }
  }

  /**
   * @private
   */
  __resetPanePosition() {
    this.paneTop = this.paneHeight = 0;
    // this.paneTop = (this.rootClientBottom - this.rootClientTop) / 2  - this.__groupPaneEl.offsetHeight - this.parentPaneTop;
  }

  __inGroupList(groupId, consumer) {
    let index = this.groupList.findIndex(i => i.id === groupId);
    if (index > -1) {
      consumer(index, this.groupList[index]);
    }
  }

  __handleGroupAdding({isRoot, groupPath}) {
    if (this.isRoot) {
      if (isRoot) {
        this.__loadRootGroups();
      } else if (groupPath.length === 2) {
        let info: any = _.head(groupPath) || {};
        let index = this.groupList.findIndex(i => i.id === info.id);
        index > -1 && this.groupList.splice(index, 1, info);
      }
    }
    this.__loadAvailableGroups().then(() => this.performSearch());
  }

  __handleGroupEdit({groupId}) {
    this.isRoot && this.__inGroupList(groupId, () => this.__loadRootGroups());
    this.__loadAvailableGroups().then(() => this.performSearch());
  }

  __handleGroupRemove({groupId, groupPath}) {
    this.__inGroupList(groupId, index => {
      this.groupList.splice(index, 1);
    });

    if (this.isRoot && groupPath.length === 2) {
      let {id} = _.head(groupPath) || {};
      this.__groupService.info({groupId: id}).response.then(info => {
        let index = this.groupList.findIndex(i => i.id === info.id);
        index > -1 && this.groupList.splice(index, 1, info);
      });
    }

    this.__loadAvailableGroups().then(() => this.performSearch());
  }

}

class GroupSelection {
  constructor(public readonly id: number = null, public readonly direct: boolean = false) {
    Object.freeze(this);
  }
}

@containerless()
@customElement('spinner')
@inlineView('<template><div class="spinner"><div></div><div></div></div></template>')
export class Spinner {
}

@inject(TaskQueue)
@bindingBehavior('spyGroups')
export class Spy {

  static target = 'updateTarget';

  constructor(private __taskQueue: TaskQueue) {
  }

  bind(binding, scope, interceptor = _.noop) {
    binding[`intercepted-${Spy.target}`] = binding[Spy.target];
    let method = binding[Spy.target].bind(binding);
    let interceptor1 = interceptor.bind(scope.bindingContext);
    binding[Spy.target] = (...rest) => {
      method(...rest);
      this.__taskQueue.queueMicroTask(interceptor1);
    };
  }

  unbind(binding, scope) {
    binding[Spy.target] = binding[`intercepted-${Spy.target}`];
    binding[`intercepted-${Spy.target}`] = null;
  }

}
